import type { FC } from "react"
import React from "react"

import { wrapInto } from "@logic/lib/utils"
import { useRecoilValue } from "recoil"

import { panelFillState } from "~/containers/LegacyDesigner/state/elements/panels"
import { areWeAfterPhase } from "~/containers/LegacyDesigner/state/phase"
import { selectedInstallAreaIdState } from "~/containers/Proposal/state/designs/installAreas"

import {
  findProjectedAreaState,
  findInsetAreaFacesState,
} from "../../../state/elements"
import { HatchPattern } from "../Panels/Patterns"

const COLORS = [
  `#f0f`,
  `#0f0`,
  `#0ff`,
  `#f00`,
  `#ff0`,
  `#fa0`,
  `#00f`,
  `#a0f`,
  `#aaa`,
]

const InsetArea_Internal: FC<{ id: string; idx: number }> = ({ id, idx }) => {
  const { nodes } = useRecoilValue(findProjectedAreaState(id))
  const { result: insetAreaFaces } = useRecoilValue(findInsetAreaFacesState(id))

  const transparency = useRecoilValue(panelFillState)

  const isAfterAttributes = useRecoilValue(areWeAfterPhase(`Roof Attributes`))
  const selectedId = useRecoilValue(selectedInstallAreaIdState)
  const isSelected = selectedId === id

  let top = Infinity,
    bottom = 0,
    left = Infinity,
    right = 0
  const joinedPoints = nodes
    .map(({ x, y }) => {
      top = Math.min(top, y)
      bottom = Math.max(bottom, y)
      left = Math.min(left, x)
      right = Math.max(right, x)

      return `${x},${y}`
    })
    .join(` `)
  const w = right - left
  const h = bottom - top
  const color = wrapInto(idx, COLORS)
  const insetJoinedFaces = insetAreaFaces.map((face) =>
    face.nodes.map(({ x, y }) => `${x},${y}`).join(` `),
  )

  const maskId = `area-inset-path-${id.replaceAll(`/`, ``)}`
  const hatchId = `${id.replaceAll(`/`, ``)}`

  return (
    <>
      <mask id={maskId}>
        <rect x={left} y={top} height={h} width={w} fill="white" />
        {insetJoinedFaces.map((points, i) => (
          <polygon points={points} key={id + `_inset_` + i} fill="black" />
        ))}
      </mask>
      <HatchPattern id={hatchId} fill={color} />
      <polygon
        style={{ pointerEvents: `none` }}
        points={joinedPoints}
        mask={`url(#${maskId})`}
        fill={
          transparency === `Transparent`
            ? `transparent`
            : isAfterAttributes
              ? isSelected
                ? `#ffbb009d`
                : `#ffbb005e`
              : `url(#pattern-hatch-${hatchId})`
        }
      />
    </>
  )
}

export const InsetArea = React.memo(InsetArea_Internal)
